import type { AxiosProgressEvent } from 'axios';
import { http } from './client';
import type {
  IngestionJobListResponse,
  IngestionJobSummary,
  RagDocumentDetail,
  RagDocumentMetadata,
  RagDocumentSummary,
  RagKnowledgeBaseInfo,
} from './types';

export interface UploadDocumentInput {
  file: File;
  metadata: RagDocumentMetadata;
  onProgress?: (loaded: number, total: number | undefined) => void;
  signal?: AbortSignal;
}

export type UploadDocumentResponse = RagDocumentSummary;

export const ragApi = {
  async uploadDocument(input: UploadDocumentInput): Promise<UploadDocumentResponse> {
    const fd = new FormData();
    fd.append('file', input.file, input.file.name);
    fd.append('metadata', JSON.stringify(input.metadata));
    const res = await http.post<UploadDocumentResponse>(
      '/v1/bot/rag/documents',
      fd,
      {
        ...(input.signal ? { signal: input.signal } : {}),
        ...(input.onProgress
          ? {
              onUploadProgress: (ev: AxiosProgressEvent) =>
                input.onProgress!(ev.loaded, ev.total),
            }
          : {}),
        timeout: 5 * 60 * 1000,
      },
    );
    return res.data;
  },

  async getKnowledgeBase(): Promise<RagKnowledgeBaseInfo> {
    const res = await http.get<RagKnowledgeBaseInfo>('/v1/bot/rag/knowledge-base');
    return res.data;
  },

  async retryKnowledgeBase(): Promise<void> {
    await http.post('/v1/bot/rag/knowledge-base/retry', {});
  },

  async listDocuments(): Promise<RagDocumentSummary[]> {
    const res = await http.get<{ items: RagDocumentSummary[] }>('/v1/bot/rag/documents');
    return res.data.items;
  },

  async getDocument(documentId: string): Promise<RagDocumentDetail> {
    const res = await http.get<RagDocumentDetail>(
      `/v1/bot/rag/documents/${encodeURIComponent(documentId)}`,
    );
    return res.data;
  },

  async deleteDocument(documentId: string): Promise<void> {
    await http.delete(`/v1/bot/rag/documents/${encodeURIComponent(documentId)}`);
  },

  async startIngestionJob(description?: string): Promise<IngestionJobSummary> {
    const res = await http.post<IngestionJobSummary>(
      '/v1/bot/rag/ingestion-jobs',
      description ? { description } : {},
    );
    return res.data;
  },

  async listIngestionJobs(
    params: { maxResults?: number; nextToken?: string } = {},
  ): Promise<IngestionJobListResponse> {
    const res = await http.get<IngestionJobListResponse>('/v1/bot/rag/ingestion-jobs', { params });
    return res.data;
  },

  async getIngestionJob(jobId: string): Promise<IngestionJobSummary> {
    const res = await http.get<IngestionJobSummary>(
      `/v1/bot/rag/ingestion-jobs/${encodeURIComponent(jobId)}`,
    );
    return res.data;
  },
};
